import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import TextField from '@mui/material/TextField';
import Content from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { CircularProgress, IconButton, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useWorkspace } from '../context/WorkspaceContext/useWorkspace';
import { useLayer } from '../LayerContext/useLayer';
import { useGoTo } from '../GoToContext/useGoTo';
import { GET_SHARE_LINK_QUERY } from '../../gql';
import { buildShareVariables } from './buildShareVariables';
import { FormattedMessage } from 'react-intl';

interface DialogContentProps {
  onCopy: (value: boolean) => void;
}

const inputProps = {
  sx: {
    '& .MuiInputBase-input': {
      cursor: 'pointer',
    },
  },
};

const buttonStyles = {
  flexShrink: 0,
};

const errorTextStyles = {
  color: 'error.main',
};

const preventMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
  event.preventDefault();
};

const DialogContent = ({ onCopy }: DialogContentProps) => {
  useEffect(() => {
    onCopy(false);
    return () => {
      onCopy(false);
    };
  }, [onCopy]);

  const {
    store: { markerPoints, sharedInitialCoordinates, loadedShareSlug },
    setLoadedShareSlug,
  } = useWorkspace();
  const { layer } = useLayer();
  const { mapRef } = useGoTo();

  const variables = useMemo(
    () =>
      loadedShareSlug
        ? null
        : buildShareVariables({
            mapRef,
            layer,
            markerPoints,
            sharedInitialCoordinates,
          }),
    [mapRef, layer, markerPoints, sharedInitialCoordinates, loadedShareSlug]
  );

  const { data, loading, error } = useQuery(GET_SHARE_LINK_QUERY, {
    variables,
    skip: !variables,
    fetchPolicy: 'network-only',
  });

  const fetchedSlug =
    typeof data?.getShareLink === 'string' ? data.getShareLink : null;

  // Persist freshly fetched slug so reopening the dialog without marker
  // changes reuses it instead of inserting another sharing row.
  useEffect(() => {
    if (!loadedShareSlug && fetchedSlug) {
      setLoadedShareSlug(fetchedSlug);
    }
  }, [loadedShareSlug, fetchedSlug, setLoadedShareSlug]);

  const slug = loadedShareSlug ?? fetchedSlug;
  const url = slug ? `https://acolyte.cz/m/${slug}` : '';

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleTextFieldClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const value = (event.nativeEvent.target as HTMLInputElement).value;
      if (!value) return;
      navigator.clipboard.writeText(value);
      onCopy(true);
    },
    [onCopy]
  );

  const handleButtonClick = useCallback(() => {
    const value = inputRef.current?.value;
    if (!value) return;
    navigator.clipboard.writeText(value);
    onCopy(true);
  }, [onCopy]);

  if (!loadedShareSlug && (loading || !variables)) {
    return (
      <Content>
        <Stack
          width="100%"
          height="100%"
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      </Content>
    );
  }

  if (!slug) {
    return (
      <Content>
        <DialogContentText fontSize={14} sx={errorTextStyles}>
          {error ? (
            <FormattedMessage
              id="map.share.error"
              defaultMessage="Could not create a share link. Please try again."
            />
          ) : (
            <FormattedMessage
              id="map.share.notReady"
              defaultMessage="Map is not ready yet. Please wait a moment and try again."
            />
          )}
        </DialogContentText>
      </Content>
    );
  }

  return (
    <Content>
      <DialogContentText fontSize={14}>
        <FormattedMessage
          id="map.share.description"
          defaultMessage="You are sharing current map view settings and selected points. Other users will not see your finds and sites."
        />
      </DialogContentText>
      <Stack gap={1} direction="row" alignItems="center">
        <TextField
          margin="dense"
          id="share-map-input"
          fullWidth
          variant="standard"
          inputRef={inputRef}
          value={url}
          InputProps={inputProps}
          onClick={handleTextFieldClick}
          onMouseDown={preventMouseDown}
        />
        <IconButton onClick={handleButtonClick} sx={buttonStyles}>
          <ContentCopyIcon />
        </IconButton>
      </Stack>
    </Content>
  );
};

export { DialogContent };
